// openapi-fetch wrapper per il backoffice.
//
// La sessione operatore è tracciata da un bearer token salvato in localStorage
// (chiave `quantica.backoffice.token`). Un middleware inietta automaticamente
// l'header `Authorization` per tutte le richieste autenticate.
//
// Uso:
//   import { api } from "@/api/client";
//   const { data, error } = await api.GET("/v1/admin/dashboard");
//
// Nota: i tipi vengono da `schema.gen.ts` (rigenerato via `npm run typegen`).
// Finché il backend non aggiunge gli endpoint admin definitivi alcuni path
// possono essere mancanti — in quel caso le pagine usano `fetch()` diretto.

import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema.gen";

export const TOKEN_STORAGE_KEY = "quantica.backoffice.token";

// In dev passa attraverso il Vite proxy (vedi vite.config.ts). In prod si
// risolve via VITE_API_BASE (es. https://api.quantica.local).
const BASE_URL = import.meta.env.VITE_API_BASE ?? "";

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: BASE_URL });
api.use(authMiddleware);

/** Helper per chiamate `fetch()` dirette quando lo schema OpenAPI è ancora
 *  incompleto. Inietta automaticamente il bearer token e gestisce le opzioni
 *  JSON di default. */
export async function authFetch(input: string, init: RequestInit = {}) {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const headers = new Headers(init.headers ?? {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(input, { ...init, headers });
}
